import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Flex, Popover, PopoverContent, PopoverTrigger, PseudoBox, Text, useDisclosure } from '@chakra-ui/core'
import { SlidersHorizontal } from 'lucide-react'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import { Button } from 'components/ui/Button/Button'
import { SearchInput } from 'components/ui/Input/SearchInput'
import { SelectMode } from './SelectItem'
import LocaleText from 'components/LocaleText'

const DEFAULT_ITEM_HEIGHT = 44
const DEFAULT_MAX_VISIBLE = 8

export interface ISelectPopoverProps<T> {
  items: T[]
  getItemId: (item: T) => string
  // Third arg carries layout context so callers can mirror their renderItem for RTL
  renderItem: (item: T, isSelected: boolean, ctx: { isRtl: boolean }) => React.ReactNode
  selectedIds: string[]
  onSubmit: (ids: string[]) => void
  mode?: SelectMode
  trigger: React.ReactElement
  searchable?: boolean
  searchPlaceholder?: string
  filterItem?: (item: T, query: string) => boolean
  // All labels are externalised so callers can pass translated strings
  submitLabel?: string | ((pendingIds: string[]) => string)
  renderControls?: (pendingIds: string[], setPendingIds: (ids: string[]) => void) => React.ReactNode
  description?: React.ReactNode
  onOpen?: () => void
  usePortal?: boolean
  appearance?: 'default' | 'compact'
  clearLabel?: string
  emptyLabel?: string
  filterLabel?: string
  // Show a "Select all / Unselect all" toggle in the header (multiple mode only)
  showSelectAll?: boolean
  selectAllLabel?: string
  unselectAllLabel?: string
  menuWidth?: number | string
  maxVisibleItems?: number
  itemHeight?: number
  placement?: 'bottom' | 'bottom-start' | 'bottom-end' | 'top' | 'top-start' | 'top-end'
  isRtl?: boolean
}

interface IRowData<T> {
  items: T[]
  // Set for O(1) lookup instead of O(n) Array.includes on every row render
  pendingIdsSet: Set<string>
  getItemId: (item: T) => string
  renderItem: (item: T, isSelected: boolean, ctx: { isRtl: boolean }) => React.ReactNode
  onToggle: (id: string) => void
  itemHeight: number
  isRtl: boolean
}

// Defined at module scope — stable reference, react-window won't remount rows on re-render
function Row<T>({ index, style, data }: ListChildComponentProps) {
  const { items, pendingIdsSet, getItemId, renderItem, onToggle, itemHeight, isRtl } = data as IRowData<T>
  const item = items[index]
  const id = getItemId(item)
  const isSelected = pendingIdsSet.has(id)

  return (
    <div style={style}>
      <PseudoBox
        as="div"
        role="checkbox"
        aria-checked={isSelected}
        tabIndex={0}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault()
            onToggle(id)
          }
        }}
        mx="4px"
        px="8px"
        h={`${itemHeight - 4}px`}
        display="flex"
        alignItems="center"
        borderRadius="6px"
        bg={isSelected ? '#F1F5F9' : 'transparent'}
        style={{ cursor: 'pointer', transition: 'background 0.1s ease-out' }}
        onClick={() => onToggle(id)}
        _hover={{ bg: isSelected ? '#E8EEF5' : '#F8FAFC' }}
      >
        {renderItem(item, isSelected, { isRtl })}
      </PseudoBox>
    </div>
  )
}

// Simple set-equality: same size and every element of a exists in b
function areSetsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every((id) => setB.has(id))
}

export const SelectPopover = <T,>({
  items,
  getItemId,
  renderItem,
  selectedIds,
  onSubmit,
  mode = 'multiple',
  trigger,
  searchable = false,
  searchPlaceholder = 'search',
  filterItem,
  submitLabel = 'Apply',
  renderControls,
  description,
  onOpen: onOpened,
  usePortal = true,
  appearance = 'default',
  clearLabel = 'Clear',
  emptyLabel = 'No results',
  filterLabel = 'Filter',
  showSelectAll = false,
  selectAllLabel = 'group_selecte_all_filter',
  unselectAllLabel = 'group_unselecte_filter',
  menuWidth = 280,
  maxVisibleItems = DEFAULT_MAX_VISIBLE,
  itemHeight = DEFAULT_ITEM_HEIGHT,
  placement = 'bottom-start',
  isRtl = false,
}: ISelectPopoverProps<T>) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [pendingIds, setPendingIds] = useState<string[]>(selectedIds)
  const [searchQuery, setSearchQuery] = useState('')
  const dir = isRtl ? 'rtl' : 'ltr'

  // Sync pending selection from committed state only when popover opens.
  // selectedIds is intentionally excluded from deps — changes while open should NOT reset the user's in-progress selection.
  useEffect(() => {
    if (isOpen) {
      setPendingIds([...selectedIds])
      setSearchQuery('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const filteredItems = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return items
    if (!filterItem) return items
    return items.filter((item) => filterItem(item, searchQuery))
  }, [items, searchable, searchQuery, filterItem])

  const handleToggle = useCallback(
    (id: string) => {
      if (mode === 'single') {
        setPendingIds([id])
      } else {
        setPendingIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]))
      }
    },
    [mode]
  )

  const handleSubmit = () => {
    onSubmit(pendingIds)
    onClose()
  }

  const listHeight = Math.max(1, Math.min(filteredItems.length, maxVisibleItems)) * itemHeight

  const pendingIdsSet = useMemo(() => new Set(pendingIds), [pendingIds])

  const itemData = useMemo<IRowData<T>>(
    () => ({ items: filteredItems, pendingIdsSet, getItemId, renderItem, onToggle: handleToggle, itemHeight, isRtl }),
    [filteredItems, pendingIdsSet, getItemId, renderItem, handleToggle, itemHeight, isRtl]
  )

  // Memoised: avoids re-computing on every render; no string allocations
  const hasChanges = useMemo(() => !areSetsEqual(pendingIds, selectedIds), [pendingIds, selectedIds])

  const allFilteredSelected = useMemo(
    () => filteredItems.length > 0 && filteredItems.every((item) => pendingIdsSet.has(getItemId(item))),
    [filteredItems, pendingIdsSet, getItemId]
  )

  const handleToggleSelectAll = useCallback(() => {
    const filteredIds = filteredItems.map(getItemId)
    if (allFilteredSelected) {
      const filteredSet = new Set(filteredIds)
      setPendingIds((prev) => prev.filter((id) => !filteredSet.has(id)))
    } else {
      setPendingIds((prev) => Array.from(new Set([...prev, ...filteredIds])))
    }
  }, [filteredItems, getItemId, allFilteredSelected])

  return (
    <Popover
      isOpen={isOpen}
      onOpen={() => {
        onOpened?.()
        onOpen()
      }}
      onClose={onClose}
      placement={placement}
      usePortal={usePortal}
      closeOnBlur
    >
      <PopoverTrigger>{trigger}</PopoverTrigger>

      <PopoverContent
        w={menuWidth}
        maxW="calc(100vw - 24px)"
        borderRadius={appearance === 'compact' ? '16px' : '10px'}
        boxShadow="0 8px 32px rgba(15,23,42,0.12), 0 1px 4px rgba(15,23,42,0.06)"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="#E2E8F0"
        overflow="hidden"
        _focus={{ outline: 'none' }}
        zIndex={1400}
        dir={dir}
      >
        {appearance === 'default' && <Box h="2px" bg="#0F172A" />}

        <Flex px="12px" pt="10px" pb="4px" align="center" justify="space-between">
          <Flex align="center" style={{ gap: '6px' }}>
            {appearance === 'default' && <SlidersHorizontal size={13} color="#64748B" />}
            <Text
              fontSize={appearance === 'compact' ? '16px' : '11px'}
              fontWeight="600"
              color={appearance === 'compact' ? '#22272a' : '#64748B'}
              style={{ textTransform: appearance === 'compact' ? 'none' : 'uppercase' }}
            >
              {filterLabel}
            </Text>
          </Flex>
          <Flex align="center" style={{ gap: '8px' }}>
            {showSelectAll && mode === 'multiple' && filteredItems.length > 0 && (
              <Text
                as="button"
                fontSize="11px"
                fontWeight="600"
                color="#0F172A"
                style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                onClick={handleToggleSelectAll}
              >
                <LocaleText text={allFilteredSelected ? unselectAllLabel : selectAllLabel} />
              </Text>
            )}
            {appearance === 'compact' && (
              <Button variant="ghost" h="28px" onClick={() => setPendingIds([])}>
                {clearLabel}
              </Button>
            )}
            {appearance === 'default' && mode === 'multiple' && pendingIds.length > 0 && (
              <Box bg="#0F172A" borderRadius="10px" px="7px" h="18px" display="inline-flex" alignItems="center">
                <Text fontSize="11px" fontWeight="600" color="white" lineHeight="1">
                  {pendingIds.length}
                </Text>
              </Box>
            )}
          </Flex>
        </Flex>

        {renderControls && (
          <Box px="12px" py="8px">
            {renderControls(pendingIds, setPendingIds)}
          </Box>
        )}
        {searchable && (
          <Box px="8px" pt="4px" pb="4px">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
              placeholder={searchPlaceholder}
              elementHeight="36px"
              direction={dir}
            />
          </Box>
        )}

        <Box py="4px">
          {filteredItems.length === 0 ? (
            <Box px="16px" py="14px" fontSize="13px" color="#94A3B8" textAlign="center">
              {emptyLabel}
            </Box>
          ) : (
            <FixedSizeList
              direction={dir}
              height={listHeight}
              itemCount={filteredItems.length}
              itemSize={itemHeight}
              width="100%"
              overscanCount={4}
              itemData={itemData}
            >
              {Row}
            </FixedSizeList>
          )}
        </Box>

        {description && (
          <Box px="16px" py="8px" fontSize="12px" color="#85909b">
            {description}
          </Box>
        )}
        <Flex
          px="8px"
          py="8px"
          borderTopWidth="1px"
          borderTopStyle="solid"
          borderTopColor="#F1F5F9"
          align="center"
          justify={appearance === 'compact' ? 'center' : 'space-between'}
        >
          {appearance === 'default' && (
            <Button
              variant="ghost"
              fontSize="13px"
              h="32px"
              px="8px"
              color="#64748B"
              isDisabled={pendingIds.length === 0}
              onClick={() => setPendingIds([])}
            >
              {clearLabel}
            </Button>
          )}
          <Button
            variant="primary"
            minW={appearance === 'compact' ? '200px' : undefined}
            fontSize="13px"
            h={appearance === 'compact' ? '36px' : '32px'}
            borderRadius={appearance === 'compact' ? '24px' : undefined}
            px="16px"
            onClick={handleSubmit}
            style={hasChanges ? { boxShadow: '0 0 0 2px rgba(15,23,42,0.15)' } : undefined}
          >
            {typeof submitLabel === 'function' ? submitLabel(pendingIds) : submitLabel}
          </Button>
        </Flex>
      </PopoverContent>
    </Popover>
  )
}
